import { isEmpty } from 'lodash';

import useIdBasedPagination from '~/hooks/useIdBasedPagination';
import type { FnolInboxRecord } from '~/server_shared/openapi/data-contracts';

// Constants
const DEFAULT_ROWS_PER_PAGE = 25;
const FETCH_IDS_URL = '/api/v1/fnol_inbox/inbox/item_ids';
const FETCH_PAGE_DATA_URL = '/api/v1/fnol_inbox/inbox/items_by_ids';

interface UseFnolAiInboxPaginationProps {
  inboxType: 'in-queue' | 'done';
  initialRowsPerPage?: number;
  sortByColumn?: string; // Only 'date_received' supported, with optional +/- prefix
  subOrganizationIds?: number[];
  includeUnknownSubOrganization?: boolean;
  lobs?: string[];
  includeUnknownLob?: boolean;
  withReadStatus?: boolean;
}

export const useFnolAiInboxPagination = ({
  inboxType,
  initialRowsPerPage = DEFAULT_ROWS_PER_PAGE,
  sortByColumn = '-date_received', // Default to descending by date
  subOrganizationIds,
  includeUnknownSubOrganization,
  lobs,
  includeUnknownLob,
  withReadStatus = false,
}: UseFnolAiInboxPaginationProps) => {
  const fetchIdsUrl = FETCH_IDS_URL;
  const fetchPageDataUrl = FETCH_PAGE_DATA_URL;

  // Build the request payload for the IDs endpoint
  const fetchIdsParams = {
    inbox_type: inboxType,
    sort_by: sortByColumn,
    ...(!isEmpty(subOrganizationIds) && { sub_organization_ids: subOrganizationIds }),
    ...(includeUnknownSubOrganization && { include_unknown_sub_organization: true }),
    ...(!isEmpty(lobs) && { lobs }),
    ...(includeUnknownLob && { include_unknown_lob: true }),
  };

  // Build the request payload for the page data endpoint
  const fetchPageDataParams = {
    with_read_status: withReadStatus,
  };

  return useIdBasedPagination<number, null, FnolInboxRecord, null>({
    fetchIdsUrl,
    fetchPageDataUrl,
    fetchIdsParams,
    fetchPageDataParams,
    initialRowsPerPage,
  });
};
